import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const MODULES = [
  { key: 'licenseKeys', label: 'License Keys' },
  { key: 'appVersions', label: 'App Versions' },
  { key: 'devices', label: 'Devices' },
  { key: 'users', label: 'Users' },
];

const emptyForm = { name: '', permissions: [] };

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/roles');
      setRoles(data.roles);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const openCreate = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };

  const openEdit = (role) => {
    setForm({ name: role.name, permissions: role.permissions.map(p => p.module) });
    setEditId(role.id);
    setError('');
    setShowModal(true);
  };

  const toggleModule = (key) =>
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.put(`/admin/roles/${editId}`, form);
      } else {
        await api.post('/admin/roles', form);
      }
      setShowModal(false);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await api.delete(`/admin/roles/${id}`);
      fetchRoles();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete role');
    }
  };

  return (
    <div>
      <div className="table-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">🛡️ Roles</span>
          <button className="action-btn" onClick={openCreate}>+ Create Role</button>
        </div>

      {loading ? <p>Loading...</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Role Name</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888' }}>No roles found</td></tr>
              ) : roles.map((role, i) => (
                <tr key={role.id}>
                  <td>{i + 1}</td>
                  <td>{role.name}</td>
                  <td>
                    {role.permissions.length === 0
                      ? <span style={{ color: '#aaa' }}>No permissions</span>
                      : role.permissions.map(p => (
                        <span key={p.module} style={{ display: 'inline-block', background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '2px 8px', fontSize: 12, marginRight: 4 }}>
                          {MODULES.find(m => m.key === p.module)?.label || p.module}
                        </span>
                      ))}
                  </td>
                  <td>
                    <button className="edit-btn" onClick={() => openEdit(role)}>Edit</button>
                    <button className="del-btn" onClick={() => handleDelete(role.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editId ? 'Edit Role' : 'Create Role'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                  placeholder="e.g. Subadmin"
                />
              </div>
              <div className="form-group">
                <label>Permissions</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                  {MODULES.map(m => (
                    <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.permissions.includes(m.key)}
                        onChange={() => toggleModule(m.key)}
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
              {error && <p style={{ color: '#ef4444', fontSize: 13 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="action-btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
